import { useForm, getFormProps, getInputProps } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useTranslation } from 'react-i18next'
import {
  data,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  type MetaFunction,
  Form,
  Link,
  useActionData,
  useSearchParams,
} from 'react-router'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Field } from '#app/components/forms.tsx'
import OnboardingShapes from '#app/components/onboarding-shapes.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { createEnrollmentForUser } from '#app/controls/users.ts'
import { getInstance, getLocale } from '#app/middleware/i18next.ts'
import { findOrganizationByAccessCode } from '#app/utils/admin-api.server.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { executeQuery } from '#app/utils/cms.server.ts'
import { isUniqueConstraintError } from '#app/utils/database.ts'
import { siteTitle } from '#app/utils/global.ts'
import { getLocalizedContent, type LocalizedContent } from '#app/utils/i18n.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { Status } from '#app/utils/status.ts'
import { getActiveEnrollments } from '#app/utils/user.server.ts'

const EnrollmentSchema = z.object({
  accessCode: z.string(),
})

export async function loader({ context }: LoaderFunctionArgs) {
  const locale = getLocale(context)
  const i18next = getInstance(context)
  const t = i18next.getFixedT(locale)
  const pageTitle = t('addEnrollment.title')

  return { pageTitle }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const currentUserId = await getUserId(request)

  if (!currentUserId) {
    throw new Response('Unauthorized', { status: 401 })
  }

  const submission = await parseWithZod(formData, {
    schema: EnrollmentSchema.transform(async data => {
      const accessCodeResult = await findOrganizationByAccessCode(
        data.accessCode,
      )

      if (accessCodeResult.status !== Status.Success) {
        return { ...data, trainingProgramName: null }
      }

      const foundAccessCode = accessCodeResult.data

      const trainingProgramId = foundAccessCode?.trainingProgramId

      if (!trainingProgramId) {
        return {
          ...data,
          status: 'error',
          message: 'no training program for access code',
        }
      }

      const trainingProgramQuery = `*[_type == 'trainingProgram' && _id == '${trainingProgramId}']{_id, title}`
      const trainingProgramResult = await executeQuery(trainingProgramQuery)
      if (trainingProgramResult.status !== Status.Success) {
        return {
          ...data,
          status: 'error',
          message: 'error fetching training program details',
          trainingProgramName: null,
        }
      }
      const trainingProgramName = trainingProgramResult.data?.[0]?.title ?? null

      const activeEnrollments = await getActiveEnrollments(currentUserId)
      const isUserEnrolled =
        activeEnrollments.find(
          enrollment => enrollment.trainingProgramId === trainingProgramId,
        ) !== undefined

      if (isUserEnrolled) {
        return {
          ...data,
          status: 'error',
          message: 'already-enrolled',
          trainingProgramName,
        }
      }

      try {
        await createEnrollmentForUser(currentUserId, trainingProgramId)

        return {
          ...data,
          status: 'success',
          message: 'enrolled',
          trainingProgramName,
        }
      } catch (error) {
        return {
          ...data,
          status: 'error',
          message: isUniqueConstraintError(error)
            ? 'enrollment-expired'
            : 'error creating enrollment',
          trainingProgramName,
        }
      }
    }),
    async: true,
  })

  if (submission.status !== 'success') {
    return data(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }

  return { status: 'success', result: submission } as const
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const pageTitle = data?.pageTitle ?? 'Error'
  return [{ title: `${pageTitle} | ${siteTitle}` }]
}

interface LastResultValue {
  status: string
  message: string
  trainingProgramName: LocalizedContent
}

interface LastResult {
  value: LastResultValue
}

export default function CreateEnrollmentRoute() {
  const actionData = useActionData<typeof action>()
  const { i18n, t } = useTranslation()
  const isPending = useIsPending()
  const [searchParams] = useSearchParams()

  const [form, fields] = useForm({
    id: 'enrollment-form',
    constraint: getZodConstraint(EnrollmentSchema),
    defaultValue: {
      accessCode: searchParams.get('accessCode') ?? '',
    },
    lastResult: actionData?.result,
    onValidate({ formData }) {
      const result = parseWithZod(formData, { schema: EnrollmentSchema })
      return result
    },
    shouldRevalidate: 'onInput',
  })

  const locale = i18n.language

  const lastResult = actionData?.result as LastResult | undefined

  const trainingProgramName =
    getLocalizedContent(lastResult?.value?.trainingProgramName, locale) ?? ''

  const message = lastResult?.value?.message ?? null

  const userEnteredInvalidAccessCode =
    lastResult?.value?.trainingProgramName === null

  return (
    <main
      id="main-content"
      className="container flex flex-col items-center justify-center pt-20 pb-32"
    >
      <Link
        to="/dashboard"
        className="link-underline text-2 text-cyan-11 mb-10 flex items-center self-start font-medium"
      >
        <Icon name="arrow-left" className="mr-3" />
        {t('addEnrollment.backToDashboard')}
      </Link>
      <div className="w-[480px]">
        {message === null && (
          <>
            <h1 className="font-poppins text-4 font-medium">
              {t('addEnrollment.accessCode.header')}
            </h1>
            <p className="text-2 mt-3">
              {t('addEnrollment.accessCode.instructions')}
            </p>

            <div className="relative">
              <Form
                className="relative mb-6 w-full"
                method="POST"
                {...getFormProps(form)}
              >
                <div className="relative">
                  <OnboardingShapes
                    circleClassNames="top-36"
                    triangleClassNames="-top-24"
                  />
                  <div className="relative flex flex-col">
                    <div className="dark rounded-6 border-mauve-4 bg-gradient-signup mt-6 mb-5 flex w-full flex-col border px-7 py-6">
                      <div className="relative z-10">
                        <Field
                          className="w-full"
                          labelProps={{
                            htmlFor: fields.accessCode.id,
                            children: t('addEnrollment.accessCode.fieldLabel'),
                            className: 'text-white',
                          }}
                          inputProps={{
                            ...getInputProps(fields.accessCode, {
                              type: 'text',
                            }),
                            autoFocus: true,
                            name: 'accessCode',
                            placeholder: t(
                              'addEnrollment.accessCode.fieldPlaceholder',
                            ),
                          }}
                          errors={fields.accessCode.errors}
                        />
                        {!fields.accessCode.errors &&
                          userEnteredInvalidAccessCode && (
                            <Icon
                              className="text-1 text-error-4 absolute right-2 bottom-[2.35rem] h-3 w-3"
                              name="cross-1"
                            />
                          )}
                      </div>
                      {!fields.accessCode.errors &&
                        userEnteredInvalidAccessCode && (
                          <div className="animate-slide-down bg-error-2 text-error-4 -mt-4 flex-1 rounded-md px-3 py-3">
                            <Icon
                              className="text-5 text-error-4 h-4 w-4 flex-none"
                              name="cross-circled"
                            >
                              <div className="text-1">
                                {t('addEnrollment.accessCode.programNotFound')}
                              </div>
                            </Icon>
                          </div>
                        )}
                    </div>
                    {!trainingProgramName && (
                      <StatusButton
                        className="button w-full"
                        status={isPending ? 'pending' : 'idle'}
                        type="submit"
                        disabled={isPending}
                      >
                        {t('continue')}
                      </StatusButton>
                    )}
                  </div>
                </div>
              </Form>
            </div>
          </>
        )}
        {message === 'already-enrolled' && (
          <Message
            header={t('addEnrollment.alreadyEnrolled.header')}
            subheader={t('addEnrollment.alreadyEnrolled.subheader')}
          />
        )}
        {message === 'enrollment-expired' && (
          <Message
            header={t('addEnrollment.enrollmentExpired.header')}
            subheader={t('addEnrollment.enrollmentExpired.subheader')}
          />
        )}
        {message === 'enrolled' && (
          <SuccessMessage trainingProgramName={trainingProgramName} />
        )}
      </div>
    </main>
  )
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />
}

type MessageProps = {
  header: string
  subheader: string
}

function Message({ header, subheader }: MessageProps) {
  const { t } = useTranslation()

  return (
    <div className="relative">
      <OnboardingShapes
        circleClassNames="top-36"
        triangleClassNames="-top-24"
      />
      <div className="relative flex flex-col gap-4 rounded-md px-6 py-3">
        <h1 className="font-poppins text-4 font-medium">{header}</h1>
        <p className="text-2">{subheader}</p>
        <Link
          className="link-button link-button--outline"
          to="/enrollments/create"
        >
          {t('addEnrollment.accessCode.tryDifferentCode')}
        </Link>
        <Link className="link-button link-button--solid" to="/dashboard">
          {t('addEnrollment.backToDashboard')}
        </Link>
      </div>
    </div>
  )
}

function SuccessMessage({
  trainingProgramName,
}: {
  trainingProgramName: string
}) {
  const { t } = useTranslation()
  return (
    <div className="relative">
      <OnboardingShapes
        circleClassNames="top-36"
        triangleClassNames="-top-24"
      />
      <div className="relative flex flex-col gap-4 rounded-md px-6 py-3">
        <h1 className="font-poppins text-4 font-medium">
          {t('addEnrollment.success.header')}
        </h1>
        <p className="text-2">
          {t('addEnrollment.success.subheader', { trainingProgramName })}
        </p>
        <Link className="link-button link-button--solid" to="/dashboard">
          {t('addEnrollment.backToDashboard')}
        </Link>
      </div>
    </div>
  )
}
